import { useTranslation } from 'react-i18next';
import { Link, Navigate, createRootRoute, createRoute, createRouter } from '@tanstack/react-router';
import { AuthGate } from '@cguard/auth';
import { Button, EmptyState, DesignSystemLabelsProvider } from '@cguard/design-system';
import { useComponentLabels } from '@cguard/i18n';
import { AppShell } from './components/app-shell';
import { LivePage } from './routes/live';
import { ExportsPage, EvidencePlayer } from './routes/exports';
import { PlaybackPage } from './routes/playback';
import { CameraAdminPage } from './routes/admin-cameras';
import { AnalyticsAdminPage } from './routes/admin-analytics';
import { parseCameraSearch } from './lib/camera-navigation';

const rootRoute = createRootRoute({ component: RootRoute, notFoundComponent: PageNotFound });

function RootRoute(): React.JSX.Element {
  return (
    <DesignSystemLabelsProvider labels={useComponentLabels()}>
      <AuthGate>
        <AppShell />
      </AuthGate>
    </DesignSystemLabelsProvider>
  );
}

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: () => <Navigate to="/live" replace />,
});

const liveRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/live',
  validateSearch: parseCameraSearch,
  component: LiveRoute,
});

function evidenceSearch(search: Record<string, unknown>): { cameraId?: string; exportId?: string; incidentId?: string; from?: string; to?: string } {
  const result: { cameraId?: string; exportId?: string; incidentId?: string; from?: string; to?: string } = parseCameraSearch(search);
  for (const key of ['exportId', 'incidentId'] as const) {
    const value = search[key]; if (typeof value === 'string' && /^[a-fA-F0-9-]{36}$/.test(value)) result[key] = value;
  }
  for (const key of ['from', 'to'] as const) { const value = search[key]; if (typeof value === 'string' && Number.isFinite(Date.parse(value))) result[key] = value; }
  return result;
}
const exportsRoute = createRoute({ getParentRoute: () => rootRoute, path: '/exports', validateSearch: evidenceSearch,
  component: () => <ExportsPage {...exportsRoute.useSearch()} /> });
const playbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/playback',
  validateSearch: evidenceSearch,
  component: PlaybackRoute,
});

function LiveRoute(): React.JSX.Element {
  const search = liveRoute.useSearch();
  return <LivePage {...(search.cameraId ? { cameraId: search.cameraId } : {})} />;
}

function PlaybackRoute(): React.JSX.Element {
  const search = playbackRoute.useSearch();
  if (search.exportId) return <EvidencePlayer key={search.exportId} id={search.exportId} />;
  return <PlaybackPage {...(search.cameraId ? { cameraId: search.cameraId } : {})} />;
}

const cameraAdminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/cameras',
  component: CameraAdminPage,
});

const analyticsAdminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/analytics',
  component: AnalyticsAdminPage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  liveRoute,
  playbackRoute,
  exportsRoute,
  cameraAdminRoute,
  analyticsAdminRoute,
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

function PageNotFound(): React.JSX.Element {
  const { t } = useTranslation('common');
  return (
    <div className="p-6">
      <EmptyState
        title={t('pageNotFound.title')}
        description={t('pageNotFound.description')}
        action={<Button size="sm" render={<Link to="/" />}>{t('pageNotFound.back')}</Button>}
      />
    </div>
  );
}
